/**
 * Reusable Domain Checker & Purchase Component
 * Powered by Spirit Domains API — includes Stripe/PayPal checkout
 */
import { useState } from 'react';
import axios from 'axios';
import { Search, Check, X, Globe, Loader2, CreditCard, ShoppingCart, Tag, Mail, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = {
  standard: 'Standard',
  premium: 'Premium',
  ultra: 'Ultra',
};

export default function DomainChecker({ 
  headline = "Need a Domain Name?",
  subtext = "Every great business starts with a great name. Check if yours is available — from $15.99/yr.",
  subtitle,
  ctaText = "Get This Domain",
  onDomainSelected = null,
  compact = false,
  lang = "en"
}) {
  const displaySubtext = subtitle || subtext;
  const [domainInput, setDomainInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [checkoutName, setCheckoutName] = useState('');
  const [checkoutPhone, setCheckoutPhone] = useState('');
  const [smsOptin, setSmsOptin] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  // Email verification step state
  const [verifyStep, setVerifyStep] = useState('form'); // 'form' | 'sent' | 'verified'
  const [verifyCode, setVerifyCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState(null);

  const checkDomain = async () => {
    if (!domainInput.trim()) return;
    setChecking(true);
    setError('');
    setResult(null);
    setShowCheckout(false);

    let domain = domainInput.trim().toLowerCase();
    if (!domain.includes('.')) domain += '.com';

    try {
      const response = await axios.post(`${API}/personal-portfolio/check-domain`, { domain });
      setResult(response.data);
      if (response.data.available) setDomainInput(response.data.domain);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error checking domain. Please try again.');
    }
    setChecking(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      checkDomain();
    }
  };

  const handlePurchase = async (paymentMethod) => {
    if (!checkoutEmail || !checkoutName) return;
    setError('');
    // Step 1: send verification code (skipped if already verified)
    if (verifyStep !== 'verified') {
      setSendingCode(true);
      setPendingPaymentMethod(paymentMethod);
      try {
        const r = await axios.post(`${API}/personal-portfolio/email-verification/send`, {
          email: checkoutEmail.trim().toLowerCase(),
          name: checkoutName,
        });
        if (r.data?.success) {
          setVerifyStep('sent');
        } else {
          setError(r.data?.detail || 'Could not send verification email — try again.');
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Could not send verification email — try again.');
      }
      setSendingCode(false);
      return;
    }
    // Step 2: already verified → proceed to checkout
    await proceedToCheckout(paymentMethod);
  };

  const verifyAndContinue = async () => {
    if (verifyCode.length !== 6 || !/^\d{6}$/.test(verifyCode)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const r = await axios.post(`${API}/personal-portfolio/email-verification/verify`, {
        email: checkoutEmail.trim().toLowerCase(),
        code: verifyCode,
      });
      if (r.data?.success) {
        setVerifyStep('verified');
        // Auto-continue to the chosen payment method
        if (pendingPaymentMethod) {
          await proceedToCheckout(pendingPaymentMethod);
        }
      } else {
        setError('Verification failed — please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Incorrect code. Please try again.');
    }
    setVerifying(false);
  };

  const proceedToCheckout = async (paymentMethod) => {
    setPurchasing(true);
    try {
      const response = await axios.post(`${API}/personal-portfolio/domain-checkout`, {
        domain: result.domain,
        email: checkoutEmail.trim().toLowerCase(),
        name: checkoutName,
        phone: checkoutPhone.trim(),
        sms_optin: !!smsOptin,
        payment_method: paymentMethod,
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Checkout failed. Please try again.');
      setPurchasing(false);
    }
  };

  const resendCode = async () => {
    setSendingCode(true);
    setError('');
    try {
      await axios.post(`${API}/personal-portfolio/email-verification/send`, {
        email: checkoutEmail.trim().toLowerCase(),
        name: checkoutName,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend code.');
    }
    setSendingCode(false);
  };

  const AvailableResult = () => {
    const ccFee = Math.round(result.price * 0.0355 * 100) / 100;
    const total = Math.round((result.price + ccFee) * 100) / 100;
    const hasPromo = result.price < result.renewal_price;
    const renewalCcFee = hasPromo ? Math.round(result.renewal_price * 0.0355 * 100) / 100 : ccFee;
    const renewalTotal = hasPromo ? Math.round((result.renewal_price + renewalCcFee) * 100) / 100 : total;
    
    return (
    <div className="mt-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-center mb-3">
          <Check className="h-8 w-8 text-blue-600 mx-auto mb-2" />
          <p className="text-blue-700 font-semibold text-lg" data-testid="domain-available-msg">{result.domain} is available!</p>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-center gap-2">
              <span className="text-blue-700 text-2xl font-bold">${result.price}<span className="text-sm font-normal">/yr</span></span>
              {hasPromo && (
                <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                  <Tag className="h-3 w-3" /> 1st year special
                </span>
              )}
            </div>
            <p className="text-slate-500 text-xs">+ ${ccFee.toFixed(2)} platform processing fee (3.55%)</p>
            <p className="text-slate-800 text-sm font-semibold">${total.toFixed(2)} today — billed annually</p>
            {hasPromo && (
              <p className="text-slate-500 text-xs">Renews at ${result.renewal_price}/yr + ${renewalCcFee.toFixed(2)} fee = ${renewalTotal.toFixed(2)}/yr</p>
            )}
          </div>
          {result.tier !== 'standard' && (
            <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 mt-1 inline-block">
              {TIER_LABELS[result.tier]} TLD
            </span>
          )}
        </div>

        {!showCheckout ? (
          <Button 
            className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold" 
            onClick={() => {
              if (onDomainSelected) {
                onDomainSelected(result.domain);
              } else {
                setShowCheckout(true);
              }
            }}
            data-testid="domain-reserve-btn"
          >
            <ShoppingCart className="h-4 w-4 mr-2" /> {ctaText} — ${total.toFixed(2)}/yr
          </Button>
        ) : verifyStep === 'sent' ? (
          // ─── Verification code step ─────────────────────────────────────
          <div className="mt-3 space-y-3" data-testid="domain-verify-form">
            <div className="bg-blue-100 border border-blue-200 rounded-lg p-3 text-center">
              <Mail className="h-6 w-6 text-blue-600 mx-auto mb-1" />
              <p className="text-blue-900 text-sm font-semibold">Check your email</p>
              <p className="text-xs text-slate-600 mt-1">We sent a 6-digit code to <strong>{checkoutEmail}</strong>. Enter it below to verify your email before checkout.</p>
            </div>
            <Input
              placeholder="000000"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              className="bg-white border-slate-300 text-slate-900 text-2xl tracking-widest text-center font-mono"
              data-testid="domain-verify-code-input"
            />
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={verifyAndContinue}
              disabled={verifying || verifyCode.length !== 6}
              data-testid="domain-verify-submit"
            >
              {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Verify & Continue to Payment
            </Button>
            <div className="flex items-center justify-between text-xs">
              <button
                onClick={resendCode}
                disabled={sendingCode}
                className="text-blue-600 hover:underline disabled:opacity-50"
                data-testid="domain-verify-resend"
              >
                {sendingCode ? 'Sending…' : 'Resend code'}
              </button>
              <button
                onClick={() => { setVerifyStep('form'); setVerifyCode(''); setError(''); setPendingPaymentMethod(null); }}
                className="text-slate-500 hover:text-blue-600"
                data-testid="domain-verify-back"
              >
                ← Use a different email
              </button>
            </div>
            {error && <p className="text-red-600 text-xs text-center">{error}</p>}
          </div>
        ) : (
          // ─── Form step (collect name/email/phone) ───────────────────────
          <div className="mt-3 space-y-3" data-testid="domain-checkout-form">
            <Input
              placeholder="Your name *"
              value={checkoutName}
              onChange={(e) => setCheckoutName(e.target.value)}
              className="bg-white border-slate-300 text-slate-900"
              data-testid="domain-checkout-name"
            />
            <Input
              placeholder="Email address *"
              type="email"
              value={checkoutEmail}
              onChange={(e) => setCheckoutEmail(e.target.value)}
              className="bg-white border-slate-300 text-slate-900"
              data-testid="domain-checkout-email"
            />
            <Input
              placeholder="Phone (optional, for SMS confirmation)"
              type="tel"
              value={checkoutPhone}
              onChange={(e) => setCheckoutPhone(e.target.value)}
              className="bg-white border-slate-300 text-slate-900"
              data-testid="domain-checkout-phone"
            />
            {checkoutPhone && (
              <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsOptin}
                  onChange={(e) => setSmsOptin(e.target.checked)}
                  className="mt-0.5"
                  data-testid="domain-checkout-sms-optin"
                />
                <span><MessageSquare className="h-3 w-3 inline mr-1" /> Text me when my domain is registered (msg & data rates may apply, reply STOP to opt out)</span>
              </label>
            )}
            <p className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
              <Mail className="h-3 w-3 inline mr-1" /> We'll email a 6-digit code to verify your address before billing — to make sure renewal notices reach you.
            </p>
            <div className="flex gap-2">
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => handlePurchase('stripe')}
                disabled={sendingCode || purchasing || !checkoutEmail || !checkoutName}
                data-testid="domain-pay-stripe"
              >
                {sendingCode || purchasing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                Pay with Card
              </Button>
              <Button
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                onClick={() => handlePurchase('paypal')}
                disabled={sendingCode || purchasing || !checkoutEmail || !checkoutName}
                data-testid="domain-pay-paypal"
              >
                {sendingCode || purchasing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                PayPal
              </Button>
            </div>
            {error && <p className="text-red-600 text-xs text-center">{error}</p>}
            <p className="text-xs text-slate-500 text-center">Annual subscription — renews automatically. Cancel anytime.</p>
            <button 
              className="text-xs text-slate-500 hover:text-blue-600 w-full text-center"
              onClick={() => setShowCheckout(false)}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
  };

  const TakenResult = () => (
    <div className="mt-4">
      <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-4">
        <div className="flex items-center justify-center gap-2 text-red-300 mb-3">
          <X className="h-5 w-5" />
          <span className="font-medium" data-testid="domain-taken-msg">{result.domain} is taken</span>
        </div>
        {result.suggestions?.length > 0 && (
          <div>
            <p className="text-slate-400 text-xs text-center mb-2">Try one of these instead:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {result.suggestions.slice(0, 5).map((s) => (
                <button
                  key={s}
                  onClick={() => { setDomainInput(s); setResult(null); setShowCheckout(false); }}
                  className="text-sm bg-slate-700 text-blue-300 rounded-full px-3 py-1 hover:bg-slate-600 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const UnverifiedResult = () => (
    <div className="mt-4" data-testid="domain-unverified">
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-center">
        <div className="flex items-center justify-center gap-2 text-amber-700 mb-1">
          <Loader2 className="h-5 w-5" />
          <span className="font-medium" data-testid="domain-unverified-msg">Couldn't verify {result.domain}</span>
        </div>
        <p className="text-slate-600 text-sm">
          {result.error || "We couldn't verify this domain's availability right now. Please try again in a moment."}
        </p>
        <Button
          className="mt-3 bg-blue-600 hover:bg-blue-700 text-white"
          onClick={checkDomain}
          disabled={checking}
          data-testid="domain-unverified-retry"
        >
          {checking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
          Try again
        </Button>
      </div>
    </div>
  );

  const renderResult = () => {
    if (!result) return null;
    if (result.unverified) return <UnverifiedResult />;
    return result.available ? <AvailableResult /> : <TakenResult />;
  };

  if (compact) {
    return (
      <div data-testid="domain-checker-compact" className="w-full">
        <div className="text-center mb-5">
          <h3 className="text-xl font-bold text-white">{headline}</h3>
          {displaySubtext && <p className="text-slate-400 text-sm mt-1">{displaySubtext}</p>}
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="domain-checker-input"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="yourbusiness.com"
              className="pl-10 bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <Button
            data-testid="domain-checker-search-btn"
            onClick={checkDomain}
            disabled={checking || !domainInput.trim()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        {renderResult()}
      </div>
    );
  }

  return (
    <Card data-testid="domain-checker" className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 shadow-lg">
      <CardContent className="pt-6">
        <div className="text-center mb-6">
          <Globe className="h-10 w-10 text-blue-600 mx-auto mb-3" />
          <h3 className="text-2xl font-bold text-slate-900">{headline}</h3>
          <p className="text-slate-600 text-sm mt-1">{displaySubtext}</p>
        </div>

        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="domain-checker-input"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={lang === 'es' ? "TuNegocio.com" : "YourBusinessName.com"}
              className="pl-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <Button
            data-testid="domain-checker-search-btn"
            onClick={checkDomain}
            disabled={checking || !domainInput.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5"
          >
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-2" /> {lang === 'es' ? 'Verificar' : 'Check Availability'}</>}
          </Button>
        </div>

        <p className="text-slate-500 text-xs text-center">
          {lang === 'es' 
            ? '.com/.net/.org desde $15.99/año • .io/.co $39.99/año • .ai $89.99/año • Configuración inmediata'
            : '.com/.net/.org from $15.99/yr • .io/.co $39.99/yr • .ai $89.99/yr • Instant setup, no hidden fees'}
        </p>

        {error && <p className="text-red-600 text-sm text-center mt-2">{error}</p>}
        {renderResult()}
      </CardContent>
    </Card>
  );
}
